import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';

dayjs.extend(isoWeek);

// Backend constants (TASKS/IT-120046/p1/readp1.md): IpfCostItemRS.Category is one of these.
export const IPF_CATEGORY = {
  PRODUCTION: 'Production',
  SUPPLIER: 'Supplier',
  TRANSPORT: 'Transport',
  PROCESSING: 'Processing'
};

// Display title per section — labels follow the client's sample sheet (image.png), not the raw backend constant.
export const COST_SECTIONS = [
  { category: IPF_CATEGORY.PRODUCTION, title: 'Production' },
  { category: IPF_CATEGORY.SUPPLIER, title: 'Supplier' },
  { category: IPF_CATEGORY.TRANSPORT, title: 'Despatch - Transport' },
  { category: IPF_CATEGORY.PROCESSING, title: 'Processing / System' }
];

// Working week for the IPF summary table: Monday -> Saturday of the week containing `date` (no Sunday row, matches sample sheet).
export function getWeekRange(date) {
  const start = dayjs(date, 'YYYY-MM-DD').startOf('isoWeek');
  const days = Array.from({ length: 6 }, (_, i) => start.add(i, 'day').format('YYYY-MM-DD'));
  return { startDate: days[0], endDate: days[days.length - 1], days };
}

export function frameKey(item) {
  return item.frameId ? `${item.ipfJobNo}-${item.frameId}` : null;
}

function countUniqueFrames(items) {
  const frameKeys = new Set();
  let itemsWithoutFrame = 0;
  items.forEach(item => {
    const key = frameKey(item);
    if (key) {
      frameKeys.add(key);
    } else {
      itemsWithoutFrame += 1;
    }
  });
  return frameKeys.size + itemsWithoutFrame;
}

function sumItemCost(items) {
  return items.reduce((sum, r) => sum + (r.itemCostPrice || 0), 0);
}

export function buildCategoryRows(records, category, days) {
  const byCategory = records.filter(r => r.category === category);

  const rows = days.map(day => {
    const dayRecords = byCategory.filter(r => r.ipfDate && dayjs(r.ipfDate).format('YYYY-MM-DD') === day);
    const whiteItems = dayRecords.filter(r => !!r.isWhite);
    const foiledItems = dayRecords.filter(r => !r.isWhite);
    return {
      date: day,
      whiteCount: countUniqueFrames(whiteItems),
      whiteTotalCost: sumItemCost(whiteItems),
      whiteItems,
      foiledCount: countUniqueFrames(foiledItems),
      foiledTotalCost: sumItemCost(foiledItems),
      foiledItems
    };
  });

  const totalWhiteItems = rows.flatMap(r => r.whiteItems);
  const totalFoiledItems = rows.flatMap(r => r.foiledItems);

  const totals = {
    date: 'TOTAL',
    whiteCount: countUniqueFrames(totalWhiteItems),
    whiteTotalCost: sumItemCost(totalWhiteItems),
    whiteItems: totalWhiteItems,
    foiledCount: countUniqueFrames(totalFoiledItems),
    foiledTotalCost: sumItemCost(totalFoiledItems),
    foiledItems: totalFoiledItems
  };

  return { rows, totals };
}

export function buildFaultCodeCounts(records) {
  const counts = {};
  records.forEach(r => {
    const code = r.pCode || 'Unknown';
    counts[code] = (counts[code] || 0) + 1;
  });

  return Object.entries(counts)
    .map(([code, count]) => ({ code, count }))
    .sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));
}

export function formatCost(value) {
  return `£${(value || 0).toFixed(2)}`;
}
